import { generateUuid } from '../../../utils/uuids'
import API, { APIResponse } from '../../../lib/commons/api'
import { WbsItemType } from '../../../domain/entity/WbsItemEntity'
import { WbsItemDetail } from '../../../lib/functions/wbsItem'
import { TreeSource } from '../../containers/BulkSheetView/lib/tree'
import { TreeRow } from '../../containers/BulkSheetView/model'
import { ProjectMemberProps } from '../../../lib/functions/projectMember'
import { DateTerm } from '../../../utils/date'
import { WbsItemStatus } from '../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus'
import { ProjectPlanCumulation } from '../../../lib/functions/projectPlan'
import { TeamProps } from '../../../lib/functions/team'
import { UserBasic } from '../../../lib/functions/user'
import { AttachmentSummary } from '../../../utils/attachment'
import { CommentSummary } from '../../../store/comments'
import { SprintDetail } from '../../../lib/functions/sprint'
import { CUSTOM_ENUM_NONE } from '../../../lib/commons/customEnum'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'
import store from '../../../store'
import {
  BoolFilter,
  CommentCreatedAtFilter,
  CommentTextFilter,
  DateFilter,
  NumberFilter,
  SelectFilter,
  TextFilter,
} from '../../containers/BulkSheetView/components/filter'
import { TicketCumulation } from '../../../lib/functions/ticketList'
import { TagForWbsItem } from '../../../lib/functions/tag'
import {
  WbsItemAdditionalPropertyValuesVO,
  wbsItemAdditionalPropertyValuesVoService,
} from '../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { WbsItemAdditionalPropertyValueFilter } from '../../containers/BulkSheetView/components/filter/wbsItemAdditionalPropertyValues'
import { WbsItemCodeGenerator } from '../../../applications/ports/wbsItemCodeService'

/**
 * API response
 */
export class ProjectPlanSkeleton implements TreeSource<ProjectPlanSkeleton> {
  uuid: string
  lockVersion: number
  children: ProjectPlanSkeleton[]
  type: WbsItemType
  wbsItemUuid: string
  body?: ProjectPlanBody

  revision?: string
  createdAt?: number
  createdBy?: UserBasic
  updatedAt?: number
  updatedBy?: UserBasic
}

export interface ProjectPlanBody {
  wbsItem: WbsItemDetail
  sprint?: SprintDetail[]
  cumulation?: ProjectPlanCumulation
  ticketCumulation?: TicketCumulation
  commentSummary?: CommentSummary
  deliverableAttachmentSummary?: AttachmentSummary
}

/**
 * RowData definition
 */
export interface ProjectPlanNewRow extends TreeRow {
  type: WbsItemType
  wbsItemUuid: string
  body?: ProjectPlanRowBody
}

export class ProjectPlanRowBody {
  wbsItem: NewWbsItemRow
  cumulation?: ProjectPlanCumulation
  ticketCumulation?: TicketCumulation
  commentSummary?: CommentSummary
  deliverableAttachmentSummary?: AttachmentSummary
  sprint?: SprintDetail

  constructor(src: ProjectPlanBody) {
    const w = src.wbsItem
    this.wbsItem = {
      projectUuid: w.projectUuid,
      uuid: w.uuid,
      lockVersion: w.lockVersion,

      code: w.code,
      type: w.type,
      wbsItemType: w.typeDto
        ? new WbsItemTypeVO(w.typeDto)
        : store.getState().project.wbsItemTypes.get(w.type)!,
      baseWbsItemType: w.baseTypeDto
        ? new WbsItemTypeVO(w.baseTypeDto)
        : store.getState().project.wbsItemTypes.get(w.type)!,
      displayName: w.displayName,
      status: w.status ? (w.status as WbsItemStatus) : undefined,
      substatus: w.substatus === CUSTOM_ENUM_NONE ? undefined : w.substatus,
      priority: w.priority === CUSTOM_ENUM_NONE ? undefined : w.priority,
      difficulty: w.difficulty === CUSTOM_ENUM_NONE ? undefined : w.difficulty,
      description: w.description ?? '',
      team: w.team,
      accountable: w.accountable,
      responsible: w.responsible,
      assignee: w.assignee,
      estimatedStoryPoint: w.estimatedStoryPoint,
      estimatedHour: w.estimatedHour,
      actualHour: w.actualHour,
      scheduledDate: {
        startDate: w.scheduledDate.startDate,
        endDate: w.scheduledDate.endDate,
      },
      actualDate: {
        startDate: w.actualDate.startDate,
        endDate: w.actualDate.endDate,
      },

      estimatedAmount: w.estimatedAmount,
      actualAmount: w.actualAmount,

      ticketType: w.ticketType === CUSTOM_ENUM_NONE ? undefined : w.ticketType,
      ticketListUuid: w.ticketListUuid,

      watchers: w.watchers,
      tags: w.tags,

      sprint: w.sprint,
      sprints: w.sprints,

      additionalPropertyValues: w.additionalPropertyValues
        ? wbsItemAdditionalPropertyValuesVoService.of(
            w.additionalPropertyValues
          )
        : undefined,

      revision: w.revision,
      updatedAt: w.updatedAt,
      updatedBy: w.updatedBy,
      createdAt: w.createdAt,
      createdBy: w.createdBy,
    }
    this.sprint = w.sprint
    this.cumulation = src.cumulation
    this.ticketCumulation = src.ticketCumulation
    this.commentSummary = src.commentSummary
    this.deliverableAttachmentSummary = src.deliverableAttachmentSummary
  }
}

export class NewWbsItemRow {
  uuid?: string
  lockVersion?: number
  projectUuid?: string

  code?: string
  type: WbsItemType
  wbsItemType: WbsItemTypeVO
  baseWbsItemType: WbsItemTypeVO
  displayName?: string
  description?: string
  team?: TeamProps
  accountable?: ProjectMemberProps
  responsible?: ProjectMemberProps
  assignee?: ProjectMemberProps
  estimatedStoryPoint?: number
  estimatedHour?: number
  actualHour?: number
  priority?: string
  difficulty?: string
  scheduledDate?: DateTerm
  actualDate?: DateTerm
  status?: WbsItemStatus
  substatus?: string

  estimatedAmount?: number
  actualAmount?: number

  ticketType?: string
  ticketListUuid?: string

  watchers?: ProjectMemberProps[]
  revision?: string
  createdAt?: number
  createdBy?: UserBasic
  updatedAt?: number
  updatedBy?: UserBasic

  tags?: TagForWbsItem[]
  sprint?: SprintDetail
  sprints?: SprintDetail[]
  additionalPropertyValues?: WbsItemAdditionalPropertyValuesVO
}

/**
 * Row data functionality
 */
export const createNewProjectPlanNewRow = (
  type: WbsItemTypeVO,
  generateCode: WbsItemCodeGenerator,
  ticketListUuid?: string
): ProjectPlanNewRow => {
  const wbsItemUuid = generateUuid()

  return {
    uuid: generateUuid(),
    type: type.isWorkgroup() ? WbsItemType.PROCESS : type.rootType,
    wbsItemUuid: wbsItemUuid,
    treeValue: [],
    body: {
      wbsItem: {
        uuid: wbsItemUuid,
        code: generateCode(),
        type: type.isWorkgroup() ? WbsItemType.PROCESS : type.rootType,
        wbsItemType: type,
        baseWbsItemType: type,
        ticketType: type.isTicket()
          ? type.code.replace('_LIST', '')
          : undefined,
        ticketListUuid,
        status: WbsItemStatus.TODO,
        scheduledDate: { startDate: undefined, endDate: undefined },
        actualDate: { startDate: undefined, endDate: undefined },
      },
      cumulation: new ProjectPlanCumulation(),
    },
  }
}

export const duplicateProjectPlanNewRow = (
  row: ProjectPlanNewRow,
  generateCode: WbsItemCodeGenerator
) => {
  const wbsItemUuid = generateUuid()
  return {
    ...row,
    uuid: generateUuid(),
    wbsItemUuid: wbsItemUuid,
    treeValue: [],
    body: {
      wbsItem: {
        ...(row.body!.wbsItem || {}),
        uuid: wbsItemUuid,
        code: generateCode(),
      },
    },
  }
}

export const duplicateProjectPlanNewRowNameOnly = (
  row: ProjectPlanNewRow,
  generateCode: WbsItemCodeGenerator
) => {
  const wbsItemUuid = generateUuid()
  return {
    ...row,
    uuid: generateUuid(),
    wbsItemUuid: wbsItemUuid,
    treeValue: [],
    body: {
      wbsItem: {
        uuid: wbsItemUuid,
        code: generateCode(),
        status: WbsItemStatus.TODO,
        displayName: row.body?.wbsItem.displayName,
        type: row.body?.wbsItem.type,
        wbsItemType: row.body?.wbsItem.wbsItemType,
        baseWbsItemType: row.body?.wbsItem.baseWbsItemType,
        ticketType: row.body?.wbsItem.ticketType,
        ticketListUuid: row.body?.wbsItem.ticketListUuid,
        scheduledDate: { startDate: undefined, endDate: undefined },
        actualDate: { startDate: undefined, endDate: undefined },
      },
      cumulation: new ProjectPlanCumulation(),
    },
  } as ProjectPlanNewRow
}

/**
 * 同名タスク行を作成する
 * 元の行の名前や各種属性をコピーしつつ、新しいタスク行を作成
 */
export const createSameNameTaskRow = (
  sourceWbsItem: NewWbsItemRow,
  taskType: WbsItemTypeVO,
  generateCode: WbsItemCodeGenerator
): ProjectPlanNewRow => {
  const newRow = createNewProjectPlanNewRow(taskType, generateCode)
  if (newRow.body?.wbsItem) {
    newRow.body.wbsItem.displayName = sourceWbsItem.displayName
    newRow.body.wbsItem.description = sourceWbsItem.description
    newRow.body.wbsItem.team = sourceWbsItem.team
    newRow.body.wbsItem.accountable = sourceWbsItem.accountable
    newRow.body.wbsItem.responsible = sourceWbsItem.responsible
    newRow.body.wbsItem.assignee = sourceWbsItem.assignee
    newRow.body.wbsItem.estimatedStoryPoint = sourceWbsItem.estimatedStoryPoint
    newRow.body.wbsItem.estimatedHour = sourceWbsItem.estimatedHour
    newRow.body.wbsItem.priority = sourceWbsItem.priority
    newRow.body.wbsItem.difficulty = sourceWbsItem.difficulty
    newRow.body.wbsItem.scheduledDate = sourceWbsItem.scheduledDate
      ? { ...sourceWbsItem.scheduledDate }
      : undefined
    newRow.body.wbsItem.estimatedAmount = sourceWbsItem.estimatedAmount
    newRow.body.wbsItem.watchers = sourceWbsItem.watchers
      ? [...sourceWbsItem.watchers]
      : undefined
    newRow.body.wbsItem.tags = sourceWbsItem.tags
      ? [...sourceWbsItem.tags]
      : undefined
    newRow.body.wbsItem.sprint = sourceWbsItem.sprint
    newRow.body.wbsItem.additionalPropertyValues =
      sourceWbsItem.additionalPropertyValues
  }
  return newRow
}

export const acceptChild = (
  parent: ProjectPlanNewRow,
  child: ProjectPlanNewRow
): boolean => {
  const parentType = parent.body?.wbsItem?.wbsItemType
  const childType = child.body?.wbsItem?.wbsItemType
  if (!parentType || !childType) return false
  return (
    childType.canBeChildOf(parentType) ||
    (childType.isWorkgroup() && parentType.isWorkgroup())
  )
}

/**
 *
 * @param params
 */
export const fetchProjectPlanSkeleton = async (params: {
  rootProjectPlanUuid?: string
  fetchBodyUuid?: string[]
  wbsItemTypes?: string[]
}): Promise<APIResponse> => {
  return API.functional.request(
    'GET',
    '/api/v1/projects/plans/skeleton',
    params
  )
}

export const fetchProjectPlanBody = async (
  projectPlanUuid: string[]
): Promise<APIResponse> => {
  return API.functional.request('GET', '/api/v1/projects/plans/body', {
    projectPlanUuid,
  })
}
export const fetchProjectPlanBodyByCode = async (
  wbsItemCode: string[]
): Promise<APIResponse> => {
  return API.functional.request('GET', '/api/v1/projects/plans/body', {
    wbsItemCode,
  })
}

export type ProjectPlanFilterRequest = {
  projectUuid: string
  type?: SelectFilter
  code?: TextFilter
  displayName?: TextFilter
  description?: TextFilter
  estimatedHour?: NumberFilter
  estimatedStoryPoint?: NumberFilter
  scheduledStartDate?: DateFilter
  scheduledEndDate?: DateFilter
  actualStartDate?: DateFilter
  actualEndDate?: DateFilter
  status?: SelectFilter
  substatus?: SelectFilter
  priority?: SelectFilter
  difficulty?: SelectFilter
  team?: SelectFilter
  accountable?: SelectFilter
  responsible?: SelectFilter
  assignee?: SelectFilter

  estimatedAmount?: NumberFilter
  actualAmount?: NumberFilter

  actualHour?: NumberFilter
  watcher?: SelectFilter
  attachment?: BoolFilter
  sprint?: SelectFilter

  commentExist?: BoolFilter
  commentText?: CommentTextFilter
  commentCreatedAt?: CommentCreatedAtFilter
  additionalPropertyValues?: WbsItemAdditionalPropertyValueFilter[]

  tags?: SelectFilter

  createdBy?: SelectFilter
  createdAt?: DateFilter
  updatedBy?: SelectFilter
  updatedAt?: DateFilter
}

export const filter = async (
  request: ProjectPlanFilterRequest
): Promise<APIResponse> => {
  return API.functional.request(
    'GET',
    '/api/v1/projects/plans/filter_by_wbs_item',
    request
  )
}

export enum ColumnQuickFilterKey {
  INITIAL = 'INITIAL',
  PLANNING = 'PLANNING',
  WORK_RESULT = 'WORK_RESULT',
  PROGRESS = 'PROGRESS',
  PRODUCTIVITY = 'PRODUCTIVITY',
  GANTT = 'GANTT',
  RESTORE = 'RESTORE',
}

export const SelectedColumnQuickFilterKey = {
  PLANNING: ColumnQuickFilterKey.PLANNING,
  WORK_RESULT: ColumnQuickFilterKey.WORK_RESULT,
  PROGRESS: ColumnQuickFilterKey.PROGRESS,
  PRODUCTIVITY: ColumnQuickFilterKey.PRODUCTIVITY,
  GANTT: ColumnQuickFilterKey.GANTT,
  NONE: 'NONE',
} as const

export type SelectedColumnQuickFilterKeyType =
  (typeof SelectedColumnQuickFilterKey)[keyof typeof SelectedColumnQuickFilterKey]

export const getEditedAncestor = (
  uuid: string,
  data: ProjectPlanNewRow[]
): any => {
  const target = data.filter(
    row => row.uuid === uuid && (row.added || row.edited)
  )
  const treeValueUuid = [...target[0].treeValue]
  if (treeValueUuid.length > 1) {
    target.pop()
    target.push(
      ...data.filter(
        row => treeValueUuid.includes(row.uuid) && (row.edited || row.added)
      )
    )
  }
  return target
}
